"use client";

import Script from "next/script";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { useEffect, useState, useRef } from "react";

import { sectionSurface, type SectionVariant } from "@/lib/section-theme";

const HERO_GRADIENT_TEXT =
  "bg-gradient-to-r from-emerald-600 via-teal-600 to-green-600 bg-clip-text text-transparent";

const SEQUENCE_EASE = [0.16, 1, 0.3, 1] as const;

export function AppointmentSection({ variant = "dark" }: { variant?: SectionVariant }) {
  const theme = sectionSurface(variant);
  const isLight = variant === "light";
  const reducedMotion = useReducedMotion();
  const [isMounted, setIsMounted] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const widgetRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.15 });

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isMounted || !sectionRef.current || !widgetRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Quand la section entre dans le viewport
          if (entry.isIntersecting) {
            const widget = widgetRef.current;
            if (!widget) return;

            // Vérifier si le widget a déjà un iframe chargé
            const hasIframe = widget.querySelector('iframe');
            
            // Si pas d'iframe, forcer le rechargement
            if (!hasIframe && typeof window !== 'undefined' && (window as any).Calendly) {
              // Nettoyer et recréer le widget
              widget.innerHTML = '';
              widget.setAttribute('data-url', 'https://calendly.com/webdifference/nouvelle-reunion?hide_gdpr_banner=1&background_color=282828&text_color=ffffff&primary_color=71ddae&month=2025-12');
              
              // Forcer Calendly à détecter le widget
              setTimeout(() => {
                // Le script Calendly devrait détecter automatiquement le widget
                // Mais on peut aussi essayer de déclencher manuellement
                if ((window as any).Calendly && (window as any).Calendly.initInlineWidget) {
                  try {
                    (window as any).Calendly.initInlineWidget({
                      url: "https://calendly.com/webdifference/nouvelle-reunion?hide_gdpr_banner=1&background_color=282828&text_color=ffffff&primary_color=71ddae&month=2025-12",
                      parentElement: widget,
                    });
                  } catch (e) {
                    // Si ça échoue, on laisse le script automatique faire son travail
                  }
                }
              }, 100);
            }
          }
        });
      },
      {
        threshold: 0.1, // Déclencher quand 10% de la section est visible
      }
    );

    observer.observe(sectionRef.current);

    return () => {
      observer.disconnect();
    };
  }, [isMounted]);

  return (
    <section ref={sectionRef} className={`${theme.sectionPlain} py-16`} suppressHydrationWarning>
      <div className="mx-auto max-w-7xl px-6" suppressHydrationWarning>
        <div className="mx-auto mb-3 flex w-full max-w-3xl flex-col items-center gap-3 text-center sm:mb-4 sm:gap-4">
          <motion.div
            initial={false}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 14 }}
            transition={
              reducedMotion
                ? { duration: 0 }
                : { duration: 0.55, ease: SEQUENCE_EASE }
            }
          >
            <h2
              className={`mx-auto max-w-4xl text-balance text-[1.5rem] font-bold leading-[1.08] tracking-[-0.04em] sm:text-3xl md:text-4xl md:leading-[1.05] ${
                isLight ? "text-[#1C1C1C]" : "text-white"
              }`}
            >
              Alors, quand est-ce qu&apos;on{" "}
              <span className={HERO_GRADIENT_TEXT}>commence ?</span>
            </h2>
          </motion.div>
        </div>
          <div 
          ref={widgetRef}
            className="calendly-inline-widget" 
          data-url="https://calendly.com/webdifference/nouvelle-reunion?hide_gdpr_banner=1&background_color=282828&text_color=ffffff&primary_color=71ddae&month=2025-12"
          style={{ minWidth: "320px", height: "700px", minHeight: "700px", width: "100%" }}
            suppressHydrationWarning
        />
        {isMounted && (
          <Script
            src="https://assets.calendly.com/assets/external/widget.js"
            strategy="afterInteractive"
          />
        )}
        </div>
      </section>
  );
}

